import { useCallback, useState, type FormEvent } from "react";

type FormStatus = "idle" | "submitting" | "success" | "error";

interface UseFormSubmitResult {
  status: FormStatus;
  message: string;
  submit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  reset: () => void;
}

export function useFormSubmit(submitAddr: string, successMessage: string): UseFormSubmitResult {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState("");

  const reset = useCallback(() => {
    setStatus("idle");
    setMessage("");
  }, []);

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form = event.currentTarget;
      const formData = new FormData(form);

      // 反垃圾字段：非空即视为机器人，静默返回通用成功状态
      const guard = String(formData.get("website_alt") ?? "").trim();
      if (guard.length > 0) {
        setStatus("success");
        setMessage(successMessage);
        form.reset();
        return;
      }

      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        const text = typeof value === "string" ? value : "";
        if (text.trim() === "") return;
        payload.append(key, text);
      });

      setStatus("submitting");
      setMessage("");

      try {
        const response = await fetch(submitAddr, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
        });

        const responseText = await response.text();
        let parsed: {
          code?: string;
          message?: string;
          meta?: { message?: string; detail?: string };
        } | null = null;
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = null;
        }

        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
        const code = parsed?.code ?? "";
        const lowered = String(serverMsg).toLowerCase();
        const isSpam = lowered.includes("spam") || lowered.includes("form data is spam");

        if (response.ok && code === "OK" && !isSpam) {
          setStatus("success");
          setMessage(successMessage);
          form.reset();
        } else {
          setStatus("error");
          setMessage(
            serverMsg && !isSpam
              ? serverMsg
              : "提交未能完成，请检查必填内容是否完整后重试，或稍后再试。",
          );
        }
      } catch {
        setStatus("error");
        setMessage("网络连接异常，内容未能送达。请检查网络后重新提交。");
      }
    },
    [submitAddr, successMessage],
  );

  return { status, message, submit, reset };
}

export default useFormSubmit;